import * as Y from 'yjs';
import type { MindMapNode, Connection, MapSettings, MapMeta, NodeLink, Attachment } from './types';
import { createRootNode, DEFAULT_SETTINGS } from './defaults';
import { generateId } from '@/utils/id';

/**
 * Wraps a Y.Doc and provides typed access to the mind map document.
 * All mutations go through this class so Yjs tracks them for undo/redo and future sync.
 */
export class MindMapDocument {
  readonly doc: Y.Doc;
  readonly undoManager: Y.UndoManager;

  private readonly nodesMap: Y.Map<Y.Map<unknown>>;
  private readonly connectionsMap: Y.Map<Y.Map<unknown>>;
  private readonly metaMap: Y.Map<unknown>;
  private readonly settingsMap: Y.Map<unknown>;

  constructor(doc?: Y.Doc) {
    this.doc = doc ?? new Y.Doc();
    this.nodesMap = this.doc.getMap('nodes') as Y.Map<Y.Map<unknown>>;
    this.connectionsMap = this.doc.getMap('connections') as Y.Map<Y.Map<unknown>>;
    this.metaMap = this.doc.getMap('meta');
    this.settingsMap = this.doc.getMap('settings');

    this.undoManager = new Y.UndoManager(
      [this.nodesMap, this.connectionsMap],
      { captureTimeout: 500 },
    );
  }

  // ── Initialization ──────────────────────────────────────────

  /** Create a fresh document with a root node. */
  initialize(title = 'New Mind Map'): string {
    const root = createRootNode('Central Idea');

    this.doc.transact(() => {
      // Meta
      this.metaMap.set('title', title);
      this.metaMap.set('rootNodeId', root.id);
      this.metaMap.set('createdAt', new Date().toISOString());
      this.metaMap.set('updatedAt', new Date().toISOString());

      // Settings
      for (const [key, value] of Object.entries(DEFAULT_SETTINGS)) {
        this.settingsMap.set(key, value);
      }

      // Root node
      this.setNode(root);
    });

    return root.id;
  }

  // ── Meta ────────────────────────────────────────────────────

  getMeta(): MapMeta {
    return {
      title: (this.metaMap.get('title') as string) ?? 'Untitled',
      rootNodeId: (this.metaMap.get('rootNodeId') as string) ?? '',
      createdAt: (this.metaMap.get('createdAt') as string) ?? '',
      updatedAt: (this.metaMap.get('updatedAt') as string) ?? '',
    };
  }

  setTitle(title: string): void {
    this.metaMap.set('title', title);
    this.metaMap.set('updatedAt', new Date().toISOString());
  }

  // ── Settings ────────────────────────────────────────────────

  getSettings(): MapSettings {
    return {
      layoutType: (this.settingsMap.get('layoutType') as MapSettings['layoutType']) ?? 'horizontal-tree',
      horizontalGap: (this.settingsMap.get('horizontalGap') as number) ?? 80,
      verticalGap: (this.settingsMap.get('verticalGap') as number) ?? 16,
      themeId: (this.settingsMap.get('themeId') as string) ?? 'default',
    };
  }

  updateSettings(partial: Partial<MapSettings>): void {
    this.doc.transact(() => {
      for (const [key, value] of Object.entries(partial)) {
        this.settingsMap.set(key, value);
      }
      this.touchUpdatedAt();
    });
  }

  // ── Node CRUD ───────────────────────────────────────────────

  getNode(id: string): MindMapNode | undefined {
    const yNode = this.nodesMap.get(id);
    if (!yNode) return undefined;
    return this.yNodeToPlain(yNode);
  }

  getAllNodes(): Map<string, MindMapNode> {
    const result = new Map<string, MindMapNode>();
    this.nodesMap.forEach((yNode, id) => {
      result.set(id, this.yNodeToPlain(yNode));
    });
    return result;
  }

  /** Internal: write a MindMapNode into the Yjs map. */
  private setNode(node: MindMapNode): void {
    const yNode = new Y.Map<unknown>();
    yNode.set('id', node.id);
    yNode.set('parentId', node.parentId);
    yNode.set('text', node.text);
    yNode.set('notes', node.notes);
    yNode.set('collapsed', node.collapsed);

    const yChildIds = new Y.Array<string>();
    yChildIds.push(node.childIds);
    yNode.set('childIds', yChildIds);

    if (node.style) {
      const yStyle = new Y.Map<unknown>();
      for (const [k, v] of Object.entries(node.style)) {
        yStyle.set(k, v);
      }
      yNode.set('style', yStyle);
    }

    if (node.position) {
      const yPos = new Y.Map<unknown>();
      yPos.set('x', node.position.x);
      yPos.set('y', node.position.y);
      yNode.set('position', yPos);
    }

    if (node.links && node.links.length > 0) {
      const yLinks = new Y.Array<Y.Map<unknown>>();
      for (const link of node.links) {
        const yLink = new Y.Map<unknown>();
        yLink.set('url', link.url);
        if (link.label !== undefined) {
          yLink.set('label', link.label);
        }
        yLinks.push([yLink]);
      }
      yNode.set('links', yLinks);
    }

    if (node.sizeOverride) {
      const ySize = new Y.Map<unknown>();
      ySize.set('width', node.sizeOverride.width);
      ySize.set('height', node.sizeOverride.height);
      yNode.set('sizeOverride', ySize);
    }

    if (node.attachments && node.attachments.length > 0) {
      const yAttachments = new Y.Array<Y.Map<unknown>>();
      for (const att of node.attachments) {
        const yAtt = new Y.Map<unknown>();
        yAtt.set('id', att.id);
        yAtt.set('fileName', att.fileName);
        yAtt.set('mimeType', att.mimeType);
        yAtt.set('s3Key', att.s3Key);
        yAtt.set('sizeBytes', att.sizeBytes);
        yAtt.set('uploadedAt', att.uploadedAt);
        yAttachments.push([yAtt]);
      }
      yNode.set('attachments', yAttachments);
    }

    this.nodesMap.set(node.id, yNode);
  }

  private yNodeToPlain(yNode: Y.Map<unknown>): MindMapNode {
    const yChildIds = yNode.get('childIds') as Y.Array<string> | undefined;
    const yStyle = yNode.get('style') as Y.Map<unknown> | undefined;
    const yPos = yNode.get('position') as Y.Map<unknown> | undefined;
    const yLinks = yNode.get('links') as Y.Array<Y.Map<unknown>> | undefined;
    const yAttachments = yNode.get('attachments') as Y.Array<Y.Map<unknown>> | undefined;
    const ySizeOverride = yNode.get('sizeOverride') as Y.Map<unknown> | undefined;

    const links: NodeLink[] | undefined = yLinks && yLinks.length > 0
      ? yLinks.toArray().map((yLink) => ({
          url: yLink.get('url') as string,
          label: yLink.get('label') as string | undefined,
        }))
      : undefined;

    const attachments: Attachment[] | undefined = yAttachments && yAttachments.length > 0
      ? yAttachments.toArray().map((yAtt) => ({
          id: yAtt.get('id') as string,
          fileName: yAtt.get('fileName') as string,
          mimeType: yAtt.get('mimeType') as string,
          s3Key: yAtt.get('s3Key') as string,
          sizeBytes: yAtt.get('sizeBytes') as number,
          uploadedAt: yAtt.get('uploadedAt') as string,
        }))
      : undefined;

    return {
      id: yNode.get('id') as string,
      parentId: (yNode.get('parentId') as string | null) ?? null,
      childIds: yChildIds ? yChildIds.toArray() : [],
      text: (yNode.get('text') as string) ?? '',
      notes: (yNode.get('notes') as string) ?? '',
      collapsed: (yNode.get('collapsed') as boolean) ?? false,
      style: yStyle
        ? {
            backgroundColor: yStyle.get('backgroundColor') as string | undefined,
            textColor: yStyle.get('textColor') as string | undefined,
            fontSize: yStyle.get('fontSize') as number | undefined,
            fontWeight: yStyle.get('fontWeight') as 'normal' | 'bold' | undefined,
            fontStyle: yStyle.get('fontStyle') as 'normal' | 'italic' | undefined,
            textDecoration: yStyle.get('textDecoration') as 'none' | 'underline' | 'line-through' | undefined,
            borderColor: yStyle.get('borderColor') as string | undefined,
          }
        : undefined,
      position: yPos
        ? { x: yPos.get('x') as number, y: yPos.get('y') as number }
        : undefined,
      links,
      attachments,
      sizeOverride: ySizeOverride
        ? { width: ySizeOverride.get('width') as number, height: ySizeOverride.get('height') as number }
        : undefined,
    };
  }

  // ── Node Operations ─────────────────────────────────────────

  addChild(parentId: string, text = '', position?: 'start' | 'end'): string {
    const newNode = { id: '', parentId, childIds: [] as string[], text, notes: '', collapsed: false };
    const insertIndex = position === 'start' ? 0 : undefined;
    const id = this.addNodeToTree(newNode, parentId, insertIndex);
    return id;
  }

  addChildAtIndex(parentId: string, text = '', index: number): string {
    const newNode = { id: '', parentId, childIds: [] as string[], text, notes: '', collapsed: false };
    const id = this.addNodeToTree(newNode, parentId, index);
    return id;
  }

  addSiblingAfter(siblingId: string, text = ''): string | undefined {
    const sibling = this.getNode(siblingId);
    if (!sibling?.parentId) return undefined;

    const parent = this.getNode(sibling.parentId);
    if (!parent) return undefined;

    const siblingIndex = parent.childIds.indexOf(siblingId);
    const newNode = { id: '', parentId: sibling.parentId, childIds: [] as string[], text, notes: '', collapsed: false };
    return this.addNodeToTree(newNode, sibling.parentId, siblingIndex + 1);
  }

  addSiblingBefore(siblingId: string, text = ''): string | undefined {
    const sibling = this.getNode(siblingId);
    if (!sibling?.parentId) return undefined;

    const parent = this.getNode(sibling.parentId);
    if (!parent) return undefined;

    const siblingIndex = parent.childIds.indexOf(siblingId);
    const newNode = { id: '', parentId: sibling.parentId, childIds: [] as string[], text, notes: '', collapsed: false };
    return this.addNodeToTree(newNode, sibling.parentId, siblingIndex);
  }

  private addNodeToTree(
    node: Omit<MindMapNode, 'id'> & { id: string },
    parentId: string,
    insertIndex?: number,
  ): string {
    const id = generateId();
    let resultId = '';

    this.doc.transact(() => {
      const fullNode: MindMapNode = { ...node, id, parentId };
      this.setNode(fullNode);

      // Add to parent's childIds
      const parentYNode = this.nodesMap.get(parentId);
      if (parentYNode) {
        const yChildIds = parentYNode.get('childIds') as Y.Array<string>;
        if (insertIndex !== undefined && insertIndex < yChildIds.length) {
          yChildIds.insert(insertIndex, [id]);
        } else {
          yChildIds.push([id]);
        }
      }

      this.touchUpdatedAt();
      resultId = id;
    });

    return resultId;
  }

  addDisconnectedNode(x: number, y: number, text = ''): string {
    const id = generateId();

    this.doc.transact(() => {
      const node: MindMapNode = {
        id,
        parentId: null,
        childIds: [],
        text,
        notes: '',
        collapsed: false,
        position: { x, y },
      };
      this.setNode(node);
      this.touchUpdatedAt();
    });

    return id;
  }

  setNodePosition(nodeId: string, x: number, y: number): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (!yNode) return;
      let yPos = yNode.get('position') as Y.Map<unknown> | undefined;
      if (!yPos) {
        yPos = new Y.Map<unknown>();
        yNode.set('position', yPos);
      }
      yPos.set('x', x);
      yPos.set('y', y);
      this.touchUpdatedAt();
    });
  }

  clearNodePosition(nodeId: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (yNode && yNode.get('position')) {
        yNode.delete('position');
        this.touchUpdatedAt();
      }
    });
  }

  setNodeSize(nodeId: string, width: number, height: number): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (!yNode) return;
      let ySize = yNode.get('sizeOverride') as Y.Map<unknown> | undefined;
      if (!ySize) {
        ySize = new Y.Map<unknown>();
        yNode.set('sizeOverride', ySize);
      }
      ySize.set('width', width);
      ySize.set('height', height);
      this.touchUpdatedAt();
    });
  }

  clearNodeSize(nodeId: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (yNode && yNode.get('sizeOverride')) {
        yNode.delete('sizeOverride');
        this.touchUpdatedAt();
      }
    });
  }

  clearAllPositionOverrides(): void {
    this.doc.transact(() => {
      this.nodesMap.forEach((yNode) => {
        // Only clear position on tree-connected nodes (those with a parent)
        if (yNode.get('parentId') && yNode.get('position')) {
          yNode.delete('position');
        }
      });
      this.touchUpdatedAt();
    });
  }

  updateNodeText(nodeId: string, text: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (yNode) {
        yNode.set('text', text);
        this.touchUpdatedAt();
      }
    });
  }

  updateNodeNotes(nodeId: string, notes: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (yNode) {
        yNode.set('notes', notes);
        this.touchUpdatedAt();
      }
    });
  }

  toggleCollapse(nodeId: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (yNode) {
        const current = yNode.get('collapsed') as boolean;
        yNode.set('collapsed', !current);
        this.touchUpdatedAt();
      }
    });
  }

  deleteNode(nodeId: string): void {
    this.doc.transact(() => {
      this.deleteNodeRecursive(nodeId);
      this.touchUpdatedAt();
    });
  }

  private deleteNodeRecursive(nodeId: string): void {
    const node = this.getNode(nodeId);
    if (!node) return;

    // Recursively delete children
    for (const childId of node.childIds) {
      this.deleteNodeRecursive(childId);
    }

    // Remove from parent's childIds
    if (node.parentId) {
      const parentYNode = this.nodesMap.get(node.parentId);
      if (parentYNode) {
        const yChildIds = parentYNode.get('childIds') as Y.Array<string>;
        for (let i = 0; i < yChildIds.length; i++) {
          if (yChildIds.get(i) === nodeId) {
            yChildIds.delete(i, 1);
            break;
          }
        }
      }
    }

    // Remove connections involving this node
    this.connectionsMap.forEach((_, connId) => {
      const conn = this.getConnection(connId);
      if (conn && (conn.sourceNodeId === nodeId || conn.targetNodeId === nodeId)) {
        this.connectionsMap.delete(connId);
      }
    });

    this.nodesMap.delete(nodeId);
  }

  moveNode(nodeId: string, newParentId: string, insertIndex?: number): void {
    const node = this.getNode(nodeId);
    if (!node) return;
    if (nodeId === newParentId) return;

    // Prevent moving a node into its own subtree
    if (this.isDescendant(newParentId, nodeId)) return;

    this.doc.transact(() => {
      // Remove from old parent
      if (node.parentId) {
        const oldParentYNode = this.nodesMap.get(node.parentId);
        if (oldParentYNode) {
          const yChildIds = oldParentYNode.get('childIds') as Y.Array<string>;
          for (let i = 0; i < yChildIds.length; i++) {
            if (yChildIds.get(i) === nodeId) {
              yChildIds.delete(i, 1);
              break;
            }
          }
        }
      }

      // Update parentId
      const yNode = this.nodesMap.get(nodeId);
      if (yNode) {
        yNode.set('parentId', newParentId);
        // Clear position override when attaching to tree
        if (yNode.get('position')) {
          yNode.delete('position');
        }
      }

      // Add to new parent
      const newParentYNode = this.nodesMap.get(newParentId);
      if (newParentYNode) {
        const yChildIds = newParentYNode.get('childIds') as Y.Array<string>;
        if (insertIndex !== undefined && insertIndex <= yChildIds.length) {
          yChildIds.insert(insertIndex, [nodeId]);
        } else {
          yChildIds.push([nodeId]);
        }
      }

      this.touchUpdatedAt();
    });
  }

  moveNodeUp(nodeId: string): void {
    const node = this.getNode(nodeId);
    if (!node?.parentId) return;

    const parentYNode = this.nodesMap.get(node.parentId);
    if (!parentYNode) return;

    const yChildIds = parentYNode.get('childIds') as Y.Array<string>;
    let index = -1;
    for (let i = 0; i < yChildIds.length; i++) {
      if (yChildIds.get(i) === nodeId) { index = i; break; }
    }
    if (index <= 0) return;

    this.doc.transact(() => {
      yChildIds.delete(index, 1);
      yChildIds.insert(index - 1, [nodeId]);
    });
  }

  moveNodeDown(nodeId: string): void {
    const node = this.getNode(nodeId);
    if (!node?.parentId) return;

    const parentYNode = this.nodesMap.get(node.parentId);
    if (!parentYNode) return;

    const yChildIds = parentYNode.get('childIds') as Y.Array<string>;
    let index = -1;
    for (let i = 0; i < yChildIds.length; i++) {
      if (yChildIds.get(i) === nodeId) { index = i; break; }
    }
    if (index < 0 || index >= yChildIds.length - 1) return;

    this.doc.transact(() => {
      yChildIds.delete(index, 1);
      yChildIds.insert(index + 1, [nodeId]);
    });
  }

  private isDescendant(nodeId: string, potentialAncestorId: string): boolean {
    let current = this.getNode(nodeId);
    while (current) {
      if (current.id === potentialAncestorId) return true;
      if (!current.parentId) return false;
      current = this.getNode(current.parentId);
    }
    return false;
  }

  // ── Node Links ─────────────────────────────────────────────

  addNodeLink(nodeId: string, url: string, label?: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (!yNode) return;

      let yLinks = yNode.get('links') as Y.Array<Y.Map<unknown>> | undefined;
      if (!yLinks) {
        yLinks = new Y.Array<Y.Map<unknown>>();
        yNode.set('links', yLinks);
      }

      const yLink = new Y.Map<unknown>();
      yLink.set('url', url);
      if (label !== undefined) {
        yLink.set('label', label);
      }
      yLinks.push([yLink]);
      this.touchUpdatedAt();
    });
  }

  removeNodeLink(nodeId: string, url: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (!yNode) return;

      const yLinks = yNode.get('links') as Y.Array<Y.Map<unknown>> | undefined;
      if (!yLinks) return;

      for (let i = 0; i < yLinks.length; i++) {
        const yLink = yLinks.get(i);
        if (yLink.get('url') === url) {
          yLinks.delete(i, 1);
          break;
        }
      }
      this.touchUpdatedAt();
    });
  }

  // ── Node Attachments ──────────────────────────────────────

  addNodeAttachment(nodeId: string, attachment: Attachment): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (!yNode) return;

      let yAttachments = yNode.get('attachments') as Y.Array<Y.Map<unknown>> | undefined;
      if (!yAttachments) {
        yAttachments = new Y.Array<Y.Map<unknown>>();
        yNode.set('attachments', yAttachments);
      }

      const yAtt = new Y.Map<unknown>();
      yAtt.set('id', attachment.id);
      yAtt.set('fileName', attachment.fileName);
      yAtt.set('mimeType', attachment.mimeType);
      yAtt.set('s3Key', attachment.s3Key);
      yAtt.set('sizeBytes', attachment.sizeBytes);
      yAtt.set('uploadedAt', attachment.uploadedAt);
      yAttachments.push([yAtt]);
      this.touchUpdatedAt();
    });
  }

  removeNodeAttachment(nodeId: string, attachmentId: string): void {
    this.doc.transact(() => {
      const yNode = this.nodesMap.get(nodeId);
      if (!yNode) return;

      const yAttachments = yNode.get('attachments') as Y.Array<Y.Map<unknown>> | undefined;
      if (!yAttachments) return;

      for (let i = 0; i < yAttachments.length; i++) {
        const yAtt = yAttachments.get(i);
        if (yAtt.get('id') === attachmentId) {
          yAttachments.delete(i, 1);
          break;
        }
      }
      this.touchUpdatedAt();
    });
  }

  // ── Connection CRUD ─────────────────────────────────────────

  getConnection(id: string): Connection | undefined {
    const yConn = this.connectionsMap.get(id);
    if (!yConn) return undefined;
    return {
      id: yConn.get('id') as string,
      sourceNodeId: yConn.get('sourceNodeId') as string,
      targetNodeId: yConn.get('targetNodeId') as string,
      label: (yConn.get('label') as string) ?? '',
      style: yConn.get('style')
        ? (yConn.get('style') as ConnectionStyle)
        : undefined,
    };
  }

  getAllConnections(): Map<string, Connection> {
    const result = new Map<string, Connection>();
    this.connectionsMap.forEach((_, id) => {
      const conn = this.getConnection(id);
      if (conn) result.set(id, conn);
    });
    return result;
  }

  addConnection(sourceNodeId: string, targetNodeId: string, label = ''): string {
    const id = generateId();

    this.doc.transact(() => {
      const yConn = new Y.Map<unknown>();
      yConn.set('id', id);
      yConn.set('sourceNodeId', sourceNodeId);
      yConn.set('targetNodeId', targetNodeId);
      yConn.set('label', label);
      this.connectionsMap.set(id, yConn);
      this.touchUpdatedAt();
    });

    return id;
  }

  removeConnection(id: string): void {
    this.doc.transact(() => {
      this.connectionsMap.delete(id);
      this.touchUpdatedAt();
    });
  }

  // ── Undo / Redo ─────────────────────────────────────────────

  undo(): void {
    this.undoManager.undo();
  }

  redo(): void {
    this.undoManager.redo();
  }

  // ── Observe ─────────────────────────────────────────────────

  /** Subscribe to any change in the document. Returns unsubscribe fn. */
  observe(callback: () => void): () => void {
    const handler = () => callback();
    this.nodesMap.observeDeep(handler);
    this.connectionsMap.observeDeep(handler);
    this.metaMap.observeDeep(handler);
    this.settingsMap.observeDeep(handler);
    return () => {
      this.nodesMap.unobserveDeep(handler);
      this.connectionsMap.unobserveDeep(handler);
      this.metaMap.unobserveDeep(handler);
      this.settingsMap.unobserveDeep(handler);
    };
  }

  // ── Serialization ───────────────────────────────────────────

  encodeState(): Uint8Array {
    return Y.encodeStateAsUpdate(this.doc);
  }

  applyState(update: Uint8Array): void {
    Y.applyUpdate(this.doc, update);
  }

  // ── Internal ────────────────────────────────────────────────

  private touchUpdatedAt(): void {
    this.metaMap.set('updatedAt', new Date().toISOString());
  }
}

// Re-export Connection style type for use in getConnection
import type { ConnectionStyle } from './types';
